import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import AssigneePicker from '../components/AssigneePicker';
import CollaboratorsPicker from '../components/CollaboratorsPicker';
import { PageHeading, NtIcon } from '../components/NotionUI';
import {
  getTicketProjects,
  createTicketProject,
  createProject,
  getProjects,
  linkTicketProject,
  updateTicketProject,
  deleteTicketProject,
  generateProjectInboundKey,
  revokeProjectInboundKey,
  getUsers,
  errorMessage,
  API_URL,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { canAction } from '../menu';
import ActionMenu from '../components/ActionMenu';
import { setProjectMenu } from '../projectMenu';
import Select from '../components/Select';
import SearchBox from '../components/KernelSearchBox';
import Highlight from '../components/KernelHighlight';
import { searchTerms, matchesAllTerms } from '../utils/kernelSearch';

// Tenere allineato a TicketProjectController::DEFAULT_PORTAL_TITLE/SUBTITLE (backend).
const DEFAULT_PORTAL_TITLE = { 'it-IT': 'Apri una richiesta di assistenza', 'en-GB': 'Open a support request' };
const DEFAULT_PORTAL_SUBTITLE = {
  'it-IT': 'Ti risponderemo il prima possibile via email.',
  'en-GB': "We'll get back to you as soon as possible by email.",
};

// Colori proposti per le code (si può comunque scegliere un colore libero).
const COLOR_CHOICES = ['#64748b', '#2563eb', '#0891b2', '#16a34a', '#d97706', '#dc2626', '#db2777', '#7c3aed'];

const emptyForm = {
  name: '',
  slug: '',
  code: '',
  description: '',
  color: '#64748b',
  default_assignee_id: null,
  default_collaborator_ids: [],
  max_resolution_days: '',
  languages: ['it-IT'],
  default_language: 'it-IT',
  default_view: 'list',
  portal_title: { ...DEFAULT_PORTAL_TITLE },
  portal_subtitle: { ...DEFAULT_PORTAL_SUBTITLE },
  linked_project_id: '',
};

// Lingue di CONTENUTO disponibili per le etichette dei campi del form
// builder — scelte dall'admin per progetto, indipendenti dalla lingua
// dell'interfaccia dell'app (fissa, it-IT/en-GB).
const CONTENT_LANGUAGES = [
  { code: 'it-IT', label: 'Italiano' },
  { code: 'en-GB', label: 'English' },
];

function TicketProjects({ user, onLogout }) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  // Card o elenco: si ricorda l'ultima scelta.
  const [view, setView] = useState(() => {
    try { return localStorage.getItem('ticket.queuesView') === 'list' ? 'list' : 'cards'; } catch (e) { return 'cards'; }
  });
  const chooseView = (next) => {
    setView(next);
    try { localStorage.setItem('ticket.queuesView', next); } catch (e) { /* preferenza non salvata */ }
  };

  // Ricerca: più parole separate da spazio, devono comparire tutte (AND), nel testo che si vede sulla scheda.
  const terms = searchTerms(search);
  const languagesOf = (p) => (p.languages || []).map((code) => code.slice(0, 2).toUpperCase()).join(' · ');
  const deadlineOf = (p) => (p.max_resolution_days ? t('tickets.daysShort', { count: p.max_resolution_days }) : t('tickets.noDeadline'));
  const inboundOf = (p) => (p.has_inbound_key ? t('tickets.inboundActive') : t('tickets.inboundOff'));
  const matchesProject = (p) => matchesAllTerms(
    [p.name, p.code || '', `/portal/${p.slug}`, languagesOf(p), deadlineOf(p), p.mailbox_email || '', inboundOf(p)].join(' '),
    terms
  );
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [agents, setAgents] = useState([]);
  const [inboundId, setInboundId] = useState(null);
  const [newInboundKey, setNewInboundKey] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [companion, setCompanion] = useState(null); // { name } della coda appena creata, per proporre il progetto
  const [notice, setNotice] = useState(null); // { name, id } del progetto creato insieme alla coda
  const canCreateProject = canAction(user, 'projects.manage');

  const fetchProjects = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getTicketProjects();
      setProjects(data.projects || []);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    if (canAction(user, 'projects.manage')) {
      getProjects('active').then((d) => setAllProjects(d.projects || [])).catch(() => {});
    }
    getUsers()
      .then((d) => setAgents(d.users || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCreate = () => {
    setInboundId(null);
    setForm(emptyForm);
    setEditId(null);
    setShowForm(true);
  };

  const startEdit = (p) => {
    setForm({
      name: p.name,
      slug: p.slug,
      code: p.code || '',
      description: p.description || '',
      color: p.color || '#64748b',
      default_assignee_id: p.default_assignee_id || null,
      default_collaborator_ids: p.default_collaborator_ids || [],
      max_resolution_days: p.max_resolution_days != null ? String(p.max_resolution_days) : '',
      languages: p.languages && p.languages.length ? p.languages : ['it-IT'],
      default_language: p.default_language || 'it-IT',
      default_view: p.default_view || 'list',
      portal_title: { ...DEFAULT_PORTAL_TITLE, ...(p.portal_title || {}) },
      portal_subtitle: { ...DEFAULT_PORTAL_SUBTITLE, ...(p.portal_subtitle || {}) },
      linked_project_id: p.linked_project_id || '',
    });
    setEditId(p.id);
    setShowForm(true);
  };

  const setPortalText = (key, lang, value) => {
    setForm((f) => ({ ...f, [key]: { ...f[key], [lang]: value } }));
  };

  const toggleLanguage = (code) => {
    setForm((f) => {
      const has = f.languages.includes(code);
      const languages = has ? f.languages.filter((l) => l !== code) : [...f.languages, code];
      if (languages.length === 0) return f;
      const default_language = languages.includes(f.default_language) ? f.default_language : languages[0];
      return { ...f, languages, default_language };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editId) {
        await updateTicketProject(editId, { ...form, linked_project_id: form.linked_project_id ? Number(form.linked_project_id) : null });
      } else {
        const created = await createTicketProject({ ...form, linked_project_id: form.linked_project_id ? Number(form.linked_project_id) : null });
        if (canCreateProject && !form.linked_project_id) setCompanion({ name: form.name, queueId: created.project.id });
      }
      setShowForm(false);
      fetchProjects();
    } catch (err) {
      setError(errorMessage(err, t('tickets.projectSaveError')));
    } finally {
      setSaving(false);
    }
  };

  const createCompanionProject = async () => {
    const { name } = companion;
    const queue = companion;
    setCompanion(null);
    try {
      const data = await createProject({ name, lang });
      await linkTicketProject(queue.queueId, data.project.id);
      fetchProjects();
      // il progetto compare subito anche nel menu laterale
      setProjectMenu((await getProjects('active')).projects || []);
      setNotice({ name, id: data.project.id });
    } catch (err) {
      setError(errorMessage(err, t('projects.saveError')));
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(t('tickets.projectDeleteConfirm', { name: p.name }))) return;
    try {
      await deleteTicketProject(p.id);
      fetchProjects();
    } catch (err) {
      setError(errorMessage(err, t('tickets.projectDeleteError')));
    }
  };

  const portalBase = (window.location.origin || '').replace(/\/$/, '');
  const apiBase = API_URL.startsWith('http') ? API_URL.replace(/\/$/, '') : `${portalBase}${API_URL}`;
  const inboundProject = projects.find((p) => p.id === inboundId) || null;

  const copyLink = async (p) => {
    try {
      await navigator.clipboard.writeText(`${portalBase}/portal/${p.slug}`);
      setCopiedId(p.id);
      setTimeout(() => setCopiedId((id) => (id === p.id ? null : id)), 1500);
    } catch (err) {
      setError(t('tickets.copyLinkError'));
    }
  };

  // Voci dei menu di ogni coda: configurazione (modulo, notifiche, casella, API) e azioni sulla coda.
  // Progetti non ancora collegati a nessuna coda (collegamento 1:1), più eventualmente quello già scelto.
  const takenProjectIds = new Set(projects.map((q) => q.linked_project_id).filter(Boolean));
  const freeProjects = (keepId = null) => allProjects.filter((pr) => !takenProjectIds.has(pr.id) || pr.id === Number(keepId));

  const linkProject = async (queue, project) => {
    setError('');
    try {
      await linkTicketProject(queue.id, project.id);
      fetchProjects();
    } catch (err) {
      setError(errorMessage(err, t('tickets.projectSaveError')));
    }
  };

  const linkItems = (queue) => {
    const free = freeProjects();
    if (free.length === 0) return [{ label: t('tickets.noFreeProjects'), disabled: true, onClick: () => {} }];
    return free.map((pr) => ({ label: pr.name, icon: undefined, onClick: () => linkProject(queue, pr), prefix: pr.icon }));
  };

  const configItems = (p) => [
    { label: t('tickets.manageForm'), icon: 'list', onClick: () => navigate(`/ticket-projects/${p.id}/fields`) },
    { label: t('tickets.notificationsShort'), icon: 'bell', onClick: () => navigate(`/ticket-projects/${p.id}/notifications`) },
    { label: t('mailbox.short'), icon: 'mail', on: p.mailbox_active, onClick: () => navigate(`/ticket-projects/${p.id}/mailbox`) },
    { label: t('tickets.inboundShort'), icon: 'key', on: p.has_inbound_key, onClick: () => openInbound(p) },
    ...(canAction(user, 'automations.manage') ? [{ label: t('tickets.automationsShort'), icon: 'zap', on: p.automations_count > 0, onClick: () => navigate(`/automations?project=${p.id}`) }] : []),
  ];
  const moreItems = (p) => [
    { label: t('common.edit'), icon: 'edit', onClick: () => startEdit(p) },
    { divider: true },
    { label: t('common.delete'), icon: 'trash', danger: true, onClick: () => handleDelete(p) },
  ];

  const openInbound = (p) => {
    setInboundId(p.id);
    setNewInboundKey(null);
  };

  const handleGenerateKey = async (p) => {
    if (p.has_inbound_key && !window.confirm(t('tickets.inboundRegenerateConfirm'))) return;
    setError('');
    try {
      const res = await generateProjectInboundKey(p.id);
      setNewInboundKey(res.inbound_api_key);
      fetchProjects();
    } catch (err) {
      setError(errorMessage(err, t('tickets.inboundError')));
    }
  };

  const handleRevokeKey = async (p) => {
    if (!window.confirm(t('tickets.inboundRevokeConfirm'))) return;
    setError('');
    try {
      await revokeProjectInboundKey(p.id);
      setNewInboundKey(null);
      fetchProjects();
    } catch (err) {
      setError(errorMessage(err, t('tickets.inboundError')));
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
      <PageHeading icon="folder" title={t('tickets.projectsTitle')} subtitle={t('tickets.projectsSubtitle')} />

      {!showForm && (
        <div className="tp-toolbar">
          <SearchBox value={search} onChange={setSearch} placeholder={t('tickets.searchProjects')} title={t('tickets.searchProjectsHint')} />
          <span className="nt-muted">{loading ? '' : t('tickets.projectsCount', { count: projects.filter(matchesProject).length })}</span>
          <span className="tp-spacer" />
          <span className="tq-seg" role="group" aria-label={t('tickets.viewCards') + ' / ' + t('tickets.viewList')}>
            <button type="button" className={view === 'cards' ? 'tq-seg-on' : ''} onClick={() => chooseView('cards')} title={t('tickets.viewCards')} aria-label={t('tickets.viewCards')} aria-pressed={view === 'cards'}>
              <NtIcon name="layout" size={16} />
            </button>
            <button type="button" className={view === 'list' ? 'tq-seg-on' : ''} onClick={() => chooseView('list')} title={t('tickets.viewList')} aria-label={t('tickets.viewList')} aria-pressed={view === 'list'}>
              <NtIcon name="list" size={16} />
            </button>
          </span>
          <button className="btn" type="button" onClick={startCreate}>
            {t('tickets.newProject')}
          </button>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {notice && (
        <div className="success-message notice-with-action">
          {t('tickets.companionDone', { name: notice.name })}{' '}
          <button type="button" className="notice-action" onClick={() => navigate(`/projects/${notice.id}`)}>{t('tickets.companionOpen')} <span aria-hidden="true">→</span></button>
        </div>
      )}
      {companion && (
        <>
          <div className="pd-modal-overlay" onClick={() => setCompanion(null)} />
          <div className="pd-modal" role="dialog" aria-label={t('tickets.companionTitle')}>
            <h3 className="pd-modal-title">{t('tickets.companionTitle')}</h3>
            <p>{t('tickets.companionText', { name: companion.name })}</p>
            <div className="pd-popover-actions">
              <button type="button" className="btn" onClick={createCompanionProject}>{t('tickets.companionYes')}</button>
              <button type="button" className="btn btn-secondary" onClick={() => setCompanion(null)}>{t('tickets.companionNo')}</button>
            </div>
          </div>
        </>
      )}

      {inboundProject && !showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body">
            <h3>{t('tickets.inboundTitle')} — {inboundProject.name}</h3>
            <p className="page-subtitle">{t('tickets.inboundHint')}</p>
            <p style={{ margin: '12px 0 4px', fontWeight: 500 }}>{t('tickets.inboundEndpoints')}</p>
            <p className="page-subtitle" style={{ margin: '8px 0 0' }}>{t('tickets.inboundCreateTicket')}</p>
            <code className="inbound-code">POST {apiBase}/inbound/tickets</code>
            <p className="page-subtitle" style={{ margin: '10px 0 0' }}>{t('tickets.inboundAddComment')}</p>
            <code className="inbound-code">POST {apiBase}/inbound/tickets/{'{id}'}/comments</code>
            {newInboundKey ? (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid var(--warning)', background: 'var(--warning-bg)' }}>
                <strong>{t('tickets.inboundKeyNewTitle')}</strong>
                <p className="page-subtitle">{t('tickets.inboundKeyNewHint')}</p>
                <code className="inbound-code">X-Api-Key: {newInboundKey}</code>
              </div>
            ) : (
              <div className={`inbound-status ${inboundProject.has_inbound_key ? 'inbound-status-active' : ''}`}>
                <span className="inbound-status-icon" aria-hidden="true">🔑</span>
                <div>
                  <strong>
                    {inboundProject.has_inbound_key ? t('tickets.inboundKeyActiveTitle') : t('tickets.inboundKeyNoneTitle')}
                  </strong>
                  <p className="page-subtitle" style={{ margin: '2px 0 0' }}>
                    {inboundProject.has_inbound_key ? t('tickets.inboundKeyActiveHint') : t('tickets.inboundKeyNoneHint')}
                  </p>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn" type="button" onClick={() => handleGenerateKey(inboundProject)}>
                {inboundProject.has_inbound_key ? t('tickets.inboundRegenerate') : t('tickets.inboundGenerate')}
              </button>
              {inboundProject.has_inbound_key && (
                <button className="btn btn-secondary" type="button" onClick={() => handleRevokeKey(inboundProject)}>
                  {t('tickets.inboundRevoke')}
                </button>
              )}
              <button className="btn btn-secondary" type="button" onClick={() => setInboundId(null)}>
                {t('tickets.inboundClose')}
              </button>
            </div>
          </div>
        </div>
      )}

      {inboundProject && !showForm ? null : !showForm ? (
        <div className="tp-grid-wrap">
          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : projects.length === 0 ? (
            <p className="nt-empty">{t('tickets.noProjects')}</p>
          ) : (
            <>
              {terms.length > 0 && !projects.some(matchesProject) && <p className="nt-empty">{t('tickets.noProjectResults')}</p>}
              {view === 'cards' ? (
                <div className="tp-cards">
                  {projects.filter(matchesProject).map((p) => (
                    <div key={p.id} className="tp-card" style={{ '--q': p.color || '#64748b' }}>
                      <div className="tp-card-head">
                        <span className="tp-dot" aria-hidden="true" />
                        <h3 className="tp-card-name"><Highlight text={p.name} terms={terms} /></h3>
                        <span className="tp-code">{p.code ? <Highlight text={p.code} terms={terms} /> : '—'}</span>
                      </div>
                      <div className="tp-link">
                        <NtIcon name="link" />
                        <a href={`${portalBase}/portal/${p.slug}`} target="_blank" rel="noopener noreferrer"><Highlight text={`/portal/${p.slug}`} terms={terms} /></a>
                        <button type="button" className="tp-mini" onClick={() => copyLink(p)} title={t('tickets.copyLink')} aria-label={t('tickets.copyLink')}>
                          {copiedId === p.id ? '✓' : <NtIcon name="copy" />}
                        </button>
                      </div>
                      {p.linked_project ? (
                        <button type="button" className="tq-linked" onClick={() => navigate(`/projects/${p.linked_project.id}`)} title={t('tickets.openLinkedProject')}>
                          <span aria-hidden="true">{p.linked_project.icon || '📋'}</span>
                          <span className="tq-linked-name">{p.linked_project.name}</span>
                          <span aria-hidden="true">→</span>
                        </button>
                      ) : (
                        canCreateProject ? (
                          <ActionMenu
                            block
                            caret={false}
                            triggerClass="tq-linked tq-linked-empty"
                            ariaLabel={t('tickets.linkProjectAction')}
                            label={<><span aria-hidden="true">+</span> <span className="tq-linked-name">{t('tickets.noLinkedProjectCard')}</span></>}
                            items={linkItems(p)}
                          />
                        ) : (
                          <div className="tq-linked tq-linked-empty tq-linked-static">
                            <span aria-hidden="true">+</span>
                            <span className="tq-linked-name">{t('tickets.noLinkedProjectCard')}</span>
                          </div>
                        )
                      )}
                      <div className="tq-kpis">
                        <button type="button" className="tq-kpi" onClick={() => navigate(`/tickets?project=${p.id}`)}>
                          <small>{t('tickets.queueOpen')}</small>
                          <strong>{p.open_count ?? 0}</strong>
                        </button>
                        <button type="button" className={'tq-kpi' + (p.overdue_count > 0 ? ' tq-kpi-warn' : '')} onClick={() => navigate(`/tickets?project=${p.id}`)}>
                          <small>{t('tickets.queueOverdue')}</small>
                          <strong>{p.overdue_count ?? 0}</strong>
                        </button>
                      </div>
                      <div className="tq-status">
                        <span className={p.mailbox_active ? 'tq-ok' : ''} title={p.mailbox_email || ''}><i aria-hidden="true" /> {t(p.mailbox_active ? 'tickets.mailboxOn' : 'tickets.mailboxOff')}</span>
                        <span className={p.has_inbound_key ? 'tq-ok' : ''}><i aria-hidden="true" /> {t('tickets.apiShort')}</span>
                        <span className={p.automations_count > 0 ? 'tq-ok' : ''}><i aria-hidden="true" /> {t('tickets.automationsShort')}</span>
                        <span><NtIcon name="globe" size={13} /> <Highlight text={languagesOf(p)} terms={terms} /></span>
                        <span><NtIcon name="clock" size={13} /> <Highlight text={deadlineOf(p)} terms={terms} /></span>
                      </div>
                      <div className="tq-foot">
                        <ActionMenu label={t('tickets.configure')} icon="sliders" items={configItems(p)} />
                        <span className="tp-spacer" />
                        <ActionMenu iconOnly ariaLabel={t('tickets.moreActions')} align="right" items={moreItems(p)} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="tq-table-wrap">
                  <table className="tq-table">
                    <thead>
                      <tr>
                        <th>{t('tickets.colQueue')}</th>
                        <th>{t('tickets.colLink')}</th>
                        <th className="tq-num">{t('tickets.queueOpen')}</th>
                        <th className="tq-num">{t('tickets.queueOverdue')}</th>
                        <th>{t('tickets.colLanguages')}</th>
                        <th>{t('tickets.colDeadline')}</th>
                        <th>{t('tickets.colMailbox')}</th>
                        <th>{t('tickets.colApi')}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {projects.filter(matchesProject).map((p) => (
                        <tr key={p.id} style={{ '--q': p.color || '#64748b' }}>
                          <td>
                            <span className="tq-name"><span className="tp-dot" aria-hidden="true" /><Highlight text={p.name} terms={terms} /><span className="tp-code">{p.code ? <Highlight text={p.code} terms={terms} /> : '—'}</span>{p.linked_project && (
                              <button type="button" className="tq-linked tq-linked-mini" onClick={() => navigate(`/projects/${p.linked_project.id}`)} title={`${t('tickets.openLinkedProject')}: ${p.linked_project.name}`}>
                                <span aria-hidden="true">{p.linked_project.icon || '📋'}</span> <span className="tq-linked-name">{p.linked_project.name}</span>
                              </button>
                            )}</span>
                          </td>
                          <td className="tq-muted">
                            <a href={`${portalBase}/portal/${p.slug}`} target="_blank" rel="noopener noreferrer"><Highlight text={`/portal/${p.slug}`} terms={terms} /></a>
                            <button type="button" className="tp-mini" onClick={() => copyLink(p)} title={t('tickets.copyLink')} aria-label={t('tickets.copyLink')}>
                              {copiedId === p.id ? '✓' : <NtIcon name="copy" />}
                            </button>
                          </td>
                          <td className="tq-num">{p.open_count ?? 0}</td>
                          <td className={'tq-num' + (p.overdue_count > 0 ? ' tq-late' : '')}>{p.overdue_count ?? 0}</td>
                          <td className="tq-muted"><Highlight text={languagesOf(p)} terms={terms} /></td>
                          <td className="tq-muted"><Highlight text={deadlineOf(p)} terms={terms} /></td>
                          <td><span className={'tq-lamp' + (p.mailbox_active ? ' tq-ok' : '')} title={p.mailbox_email || t(p.mailbox_active ? 'tickets.mailboxOn' : 'tickets.mailboxOff')}><i aria-hidden="true" /></span></td>
                          <td><span className={'tq-lamp' + (p.has_inbound_key ? ' tq-ok' : '')} title={t(p.has_inbound_key ? 'tickets.apiOn' : 'tickets.apiOff')}><i aria-hidden="true" /></span></td>
                          <td className="tq-actions">
                            <ActionMenu label={t('tickets.configure')} icon="sliders" items={configItems(p)} align="right" />
                            <ActionMenu iconOnly ariaLabel={t('tickets.moreActions')} align="right" items={moreItems(p)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="tp-form">
          <h2 className="tp-form-title">{editId ? t('tickets.editProject') : t('tickets.newProject')}</h2>

          <section className="tp-section">
            <h3>{t('tickets.sectionGeneral')}</h3>
            <div className="tp-fields">
              <div className="form-group">
                <label>{t('tickets.projectName')}</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              {canAction(user, 'projects.manage') && (
                <div className="form-group">
                  <label>{t('tickets.linkedProject')}</label>
                  <Select value={form.linked_project_id} onChange={(e) => setForm({ ...form, linked_project_id: e.target.value })}>
                    <option value="">{t('tickets.noLinkedProject')}</option>
                    {freeProjects(form.linked_project_id).map((pr) => <option key={pr.id} value={pr.id}>{pr.icon ? `${pr.icon} ${pr.name}` : pr.name}</option>)}
                  </Select>
                  <p className="tp-hint">{t('tickets.linkedProjectHint')}</p>
                </div>
              )}
              <div className="form-group">
                <label>{t('tickets.projectCode')}</label>
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().slice(0, 4) })}
                  maxLength={4}
                  required
                />
                <p className="tp-hint">{t('tickets.projectCodeHint')}</p>
              </div>
              <div className="form-group tp-full">
                <label>{t('tickets.projectSlug')}</label>
                <input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder={t('tickets.projectSlugPlaceholder')}
                />
                <p className="tp-hint">{t('tickets.projectSlugHint')}</p>
              </div>
              <div className="form-group tp-full">
                <label>{t('tickets.projectDescription')}</label>
                <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="form-group tp-full">
                <label>{t('tickets.projectColor')}</label>
                <div className="tp-swatches">
                  {COLOR_CHOICES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={'tp-swatch' + (form.color.toLowerCase() === c ? ' tp-swatch-on' : '')}
                      style={{ background: c }}
                      aria-label={c}
                      aria-pressed={form.color.toLowerCase() === c}
                      onClick={() => setForm({ ...form, color: c })}
                    />
                  ))}
                  <input type="color" className="tp-color-input" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} aria-label={t('tickets.projectColor')} />
                </div>
              </div>
            </div>
          </section>

          <section className="tp-section">
            <h3>{t('tickets.sectionHandling')}</h3>
            <div className="tp-fields">
              <div className="form-group tp-full">
                <label>{t('tickets.defaultAssignee')}</label>
                <AssigneePicker
                  me={user.id}
                  value={form.default_assignee_id}
                  agents={agents}
                  onChange={(assigneeId) => setForm({
                    ...form,
                    default_assignee_id: assigneeId,
                    default_collaborator_ids: form.default_collaborator_ids.filter((cid) => cid !== assigneeId),
                  })}
                />
                <p className="tp-hint">{t('tickets.defaultAssigneeHint')}</p>
              </div>
              <div className="form-group tp-full">
                <label>{t('tickets.defaultCollaborators')}</label>
                <CollaboratorsPicker
                  me={user.id}
                  collaborators={form.default_collaborator_ids
                    .map((cid) => agents.find((a) => a.id === cid))
                    .filter(Boolean)
                    .map((a) => ({ id: a.id, name: a.name, surname: a.surname || '' }))}
                  agents={agents}
                  primaryId={form.default_assignee_id}
                  onChange={(ids) => setForm({ ...form, default_collaborator_ids: ids })}
                />
                <p className="tp-hint">{t('tickets.defaultCollaboratorsHint')}</p>
              </div>
              <div className="form-group">
                <label>{t('tickets.maxResolutionDays')}</label>
                <input
                  type="number"
                  min="1"
                  value={form.max_resolution_days}
                  onChange={(e) => setForm({ ...form, max_resolution_days: e.target.value })}
                  placeholder={t('tickets.maxResolutionDaysPlaceholder')}
                />
                <p className="tp-hint">{t('tickets.maxResolutionDaysHint')}</p>
              </div>
              <div className="form-group">
                <label>{t('tickets.defaultView')}</label>
                <Select value={form.default_view} onChange={(e) => setForm({ ...form, default_view: e.target.value })}>
                  <option value="list">{t('tickets.viewList')}</option>
                  <option value="board">{t('tickets.viewBoard')}</option>
                </Select>
                <p className="tp-hint">{t('tickets.defaultViewProjectHint')}</p>
              </div>
            </div>
          </section>

          <section className="tp-section">
            <h3>{t('tickets.sectionPortal')}</h3>
            <div className="tp-fields">
              <div className="form-group tp-full">
                <label>{t('tickets.projectLanguages')}</label>
                <p className="tp-hint">{t('tickets.projectLanguagesHint')}</p>
                <div className="tp-langs">
                  {CONTENT_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      className={'tpl-cat' + (form.languages.includes(lang.code) ? ' tpl-cat-active' : '')}
                      aria-pressed={form.languages.includes(lang.code)}
                      onClick={() => toggleLanguage(lang.code)}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>{t('tickets.defaultLanguage')}</label>
                <Select
                  value={form.default_language}
                  onChange={(e) => setForm({ ...form, default_language: e.target.value })}
                >
                  {form.languages.map((code) => (
                    <option key={code} value={code}>
                      {CONTENT_LANGUAGES.find((l) => l.code === code)?.label || code}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="form-group tp-full">
                <label>{t('tickets.portalTitle')}</label>
                <p className="tp-hint">{t('tickets.portalTitleHint')}</p>
                {form.languages.map((code) => (
                  <input
                    key={code}
                    value={form.portal_title[code] || ''}
                    onChange={(e) => setPortalText('portal_title', code, e.target.value)}
                    placeholder={CONTENT_LANGUAGES.find((l) => l.code === code)?.label || code}
                    style={{ marginBottom: 6 }}
                  />
                ))}
              </div>
              <div className="form-group tp-full">
                <label>{t('tickets.portalSubtitle')}</label>
                {form.languages.map((code) => (
                  <input
                    key={code}
                    value={form.portal_subtitle[code] || ''}
                    onChange={(e) => setPortalText('portal_subtitle', code, e.target.value)}
                    placeholder={CONTENT_LANGUAGES.find((l) => l.code === code)?.label || code}
                    style={{ marginBottom: 6 }}
                  />
                ))}
              </div>
            </div>
          </section>

          <div className="tp-savebar">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      )}
      </div>
    </Layout>
  );
}

export default TicketProjects;
